// ── program-lifecycle-modal: the publish + archive confirm Modals ──────────
// Wave 2 (Programs). The program lifecycle is a state machine driven entirely by
// PATCH status: draft -> active (PUBLISH) -> archived (ARCHIVE). There is NO
// destructive delete. Each transition is a deliberate confirmation step so it is
// never a one-click accident; the page owns the actual PATCH and toasts only
// after the backend confirms. A refused write (permission-denied / gate-closed /
// unavailable) reads as its honest message inline and the dialog stays open — a
// closed write NEVER implies the transition happened.

import { Alert, Button, Modal } from "@vjal/ui";

import { destructiveButtonClass } from "../../components/ui-classes";
import type { CoachingProgramDto } from "../../services/coaching-dtos";
import { describeProgramWriteError } from "./programs-write";

export type ProgramLifecycleAction = "publish" | "archive";

export interface ProgramLifecycleModalProps {
  open: boolean;
  action: ProgramLifecycleAction;
  program: CoachingProgramDto | null;
  onClose: () => void;
  /** The actual transition PATCH — owned by the page (react-query mutation). */
  onConfirm: () => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

const COPY: Record<ProgramLifecycleAction, { title: string; confirmLabel: string; tone: "warning"; body: (name: string) => string }> = {
  publish: {
    title: "Publish program",
    confirmLabel: "Publish program",
    tone: "warning",
    body: (name) =>
      `Publishing ${name} sets it Active so cohorts can be assigned to it. You can archive it later from its detail page.`
  },
  archive: {
    title: "Archive program",
    confirmLabel: "Archive program",
    tone: "warning",
    body: (name) =>
      `Archiving ${name} removes it from active navigation. Cohorts already assigned to it stay intact; you can reactivate it later from its edit form.`
  }
};

export function ProgramLifecycleModal({
  open,
  action,
  program,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: ProgramLifecycleModalProps) {
  const topError = describeProgramWriteError(submitError);
  const name = program?.name || "this program";
  const copy = COPY[action];

  return (
    <Modal open={open} onClose={onClose} title={copy.title}>
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Alert variant={copy.tone}>{copy.body(name)}</Alert>

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={action === "archive" ? "danger" : "primary"}
            className={action === "archive" ? destructiveButtonClass : undefined}
            isLoading={submitting}
            onClick={() => {
              void onConfirm().catch(() => {
                // The owning mutation surfaces the error via `submitError`;
                // swallow so the dialog stays open with the inline error.
              });
            }}
          >
            {copy.confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
